/* Jump About. Orphan delivery-fee reconciler.
 * Go-live loop row `cart-orphan-delivery-fee`, 2026-07-28.
 *
 * THE DEFECT. Build a cart with a product plus a delivery fee, then remove the
 * product. What is left is a cart of 1 item, total $110, a single Delivery line,
 * with the delivery_suburb and delivery_fee attributes still set and a live
 * Checkout button. sections/main-cart.liquid already guards the MIRROR case
 * (products present, no delivery) with a warning plus an inline suburb
 * calculator, so the intent to guard this pair exists; only the reverse
 * condition was ever written. BoomGate, the server-side validation app, keys on
 * the PRESENCE of the delivery_suburb attribute, so it does not block this shape
 * either.
 *
 * IAN'S DIRECTION (2026-07-28): auto-remove the delivery fee line when no
 * products remain, so the cart empties cleanly.
 *
 * MECHANISM, and why it is shaped this way.
 *  - EVENT-DRIVEN, never a poll. Three triggers, all free:
 *      1. the server-rendered tally at page load (window.JACartTally, computed
 *         in layout/theme.liquid, so no request is needed to know the state),
 *      2. the theme's own `cart:change` CustomEvent, which carries the full
 *         parsed cart and is dispatched by assets/cart.js after every line
 *         change and by assets/global.js after every add,
 *      3. the cart drawer gaining its `open` attribute, watched by a
 *         MutationObserver scoped to that one element and one attribute.
 *  - ONE WRITE. A single /cart/update.js zeroes all 11 JA-DELIVERY variants and
 *    blanks both attributes, and carries `sections` so the SAME response returns
 *    the repainted HTML (bundled section rendering). No /cart.js read, no
 *    change-loop, no page reload. The 429 / Cloudflare request hygiene the
 *    suburb widget was rewritten for applies here too.
 *  - The saved suburb is cleared from localStorage in the same pass. The cart's
 *    delivery_suburb attribute is gone, so a widget still showing
 *    "Terrigal (2260)  Delivery: $110" would be exactly the widget-vs-cart
 *    divergence the cart-function row fixed. Widget state and cart state stay
 *    equal, and any mounted widget is reset to its search state so the customer
 *    can pick again in two clicks.
 *  - NO SUBMIT INTERCEPTION anywhere. During the write the checkout controls are
 *    disabled presentationally by a class on <html>, never by cancelling a form
 *    submit. That is the rule that broke add-to-cart on this store once already.
 */
(function () {
  'use strict';

  var SKU_MARK = 'JA-DELIVERY';
  var STORAGE_KEY = 'jumpabout_suburb';
  var ATTR_SUBURB = 'delivery_suburb';
  var ATTR_FEE = 'delivery_fee';
  var BUSY_CLASS = 'ja-cart-reconciling';

  // All 11 live JA-DELIVERY variant IDs, product 10050541093113, store
  // 16xsd9-0b. Re-verified against the Admin API 2026-07-28: 11 variants,
  // SKUs JA-DELIVERY-60 through JA-DELIVERY-160, all requires_shipping false.
  var DELIVERY_VARIANT_IDS = [
    48368985932025, // $60
    48369111597305, // $70
    48369111630073, // $80
    48369111662841, // $90
    48369111695609, // $100
    48369111728377, // $110
    48369111761145, // $120
    48369111793913, // $130
    48369111826681, // $140
    48369111859449, // $150
    48369111892217  // $160
  ];

  // Same three sections the theme's own `cart:refresh` handler repaints.
  var SECTION_MAP = [
    { id: 'cart-icon-bubble', selector: '.cart-count-bubble' },
    { id: 'cart-drawer', selector: '#CartDrawer' },
    { id: 'main-cart', selector: '.main-cart' }
  ];

  var busy = false;
  // Best known cart tally. Seeded server-side at page load, then kept current by
  // every `cart:change`. Means the drawer trigger needs no request of its own.
  var known = window.JACartTally && typeof window.JACartTally.delivery === 'number'
    ? { products: window.JACartTally.products, delivery: window.JACartTally.delivery }
    : null;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function isDeliveryLine(item) {
    if (!item) return false;
    if (item.sku && String(item.sku).indexOf(SKU_MARK) !== -1) return true;
    var id = Number(item.variant_id || item.id);
    return DELIVERY_VARIANT_IDS.indexOf(id) !== -1;
  }

  function tally(items) {
    var t = { products: 0, delivery: 0 };
    (items || []).forEach(function (item) {
      if (isDeliveryLine(item)) t.delivery += 1;
      else t.products += 1;
    });
    return t;
  }

  function isOrphan(t) {
    return !!t && t.delivery > 0 && t.products === 0;
  }

  function clearSavedSuburb() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* private mode */ }
  }

  // Reset any mounted suburb widget from its "selected" state back to the search
  // input, so the widget never claims a suburb the cart no longer carries.
  // Class names read from assets/delivery-suburb-selector.js, not guessed.
  function resetWidgets() {
    document.querySelectorAll('.ja-suburb-widget').forEach(function (w) {
      var selected = w.querySelector('.ja-suburb-widget__selected');
      var pick = w.querySelector('[data-pick]');
      var input = w.querySelector('.ja-suburb-widget__input');
      var results = w.querySelector('.ja-suburb-widget__results');
      if (selected) selected.hidden = true;
      if (pick) pick.style.display = '';
      if (input) {
        input.value = '';
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }
      if (results) results.hidden = true;
    });
  }

  function repaint(sections) {
    var applied = false;
    SECTION_MAP.forEach(function (section) {
      var html = sections && sections[section.id];
      if (!html) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var source = parsed.querySelector(section.selector);
      var destinations = document.querySelectorAll(section.selector);
      if (!source || !destinations.length) return;
      destinations.forEach(function (destination) {
        destination.innerHTML = source.innerHTML;
        if (source.classList.contains('is-empty')) destination.classList.add('is-empty');
        else destination.classList.remove('is-empty');
      });
      applied = true;
    });
    // Fallback only. The theme's own handler re-fetches the same three sections.
    if (!applied) document.dispatchEvent(new CustomEvent('cart:refresh'));
    resetWidgets();
  }

  function removeOrphanDelivery(reason) {
    if (busy) return;
    busy = true;
    document.documentElement.classList.add(BUSY_CLASS);

    var updates = {};
    DELIVERY_VARIANT_IDS.forEach(function (id) { updates[id] = 0; });

    var body = {
      updates: updates,
      attributes: {},
      sections: SECTION_MAP.map(function (s) { return s.id; }).join(','),
      sections_url: root() + 'cart'
    };
    body.attributes[ATTR_SUBURB] = '';
    body.attributes[ATTR_FEE] = '';

    fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        known = tally(cart && cart.items);
        clearSavedSuburb();
        repaint(cart && cart.sections);
        // eslint-disable-next-line no-console
        console.info('[JumpAbout] orphan delivery fee removed (' + reason + ')');
      })
      .catch(function (error) {
        // eslint-disable-next-line no-console
        console.warn('[JumpAbout] orphan delivery reconcile failed:', error);
      })
      .then(function () {
        busy = false;
        document.documentElement.classList.remove(BUSY_CLASS);
      });
  }

  function evaluate(t, reason) {
    if (!t) return;
    known = t;
    if (isOrphan(t)) removeOrphanDelivery(reason);
  }

  // TRIGGER 2. The theme's cart-change path. assets/cart.js dispatches this
  // after every quantity change and every remove, on the cart page and in the
  // drawer; assets/global.js dispatches it after every add.
  document.addEventListener('cart:change', function (event) {
    var cart = event && event.detail && event.detail.cart;
    if (!cart) return;
    evaluate(tally(cart.items), 'cart:change');
  });

  // The suburb widget writes the cart with no theme repaint and no theme event
  // (see assets/delivery-suburb-selector.js). Without this the tally would stay
  // frozen at whatever the page load reported, and TRIGGER 3 below would miss an
  // orphan created after load, which is the single most likely way to reach one:
  // pick a suburb, never add a castle, open the cart. Deliberately updates the
  // tally ONLY. It does not act, because at the instant of a pick the customer is
  // mid-funnel and deleting the fee they just chose would be hostile.
  document.addEventListener('ja:delivery-line-count', function (event) {
    var count = event && event.detail && event.detail.delivery;
    if (typeof count !== 'number') return;
    if (!known) known = { products: 0, delivery: 0 };
    known.delivery = count;
  });

  // TRIGGER 3. Drawer open. Scoped to the one element and the one attribute, so
  // it cannot become the unscoped observer that caused the init storm here once.
  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(function () {
      if (drawer.hasAttribute('open') && isOrphan(known)) {
        removeOrphanDelivery('drawer-open');
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  // TRIGGER 1. Page load, from the server-rendered tally. Zero requests.
  function start() {
    watchDrawer();
    if (isOrphan(known)) removeOrphanDelivery('page-load');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
